/**
 * Pure consent resolution (RQ78378 / RQ78379).
 *
 * No DOM, no storage, no side effects — unit-testable in isolation. These are
 * the rules the analytics gate and the banner-visibility logic both depend on.
 */

import type { ConsentCategories, ConsentCategory, Jurisdiction } from './consentTypes'
import type { Tracker } from './trackers'

/** Map a category to its stored boolean (strictly_necessary is implicit true). */
function categoryConsented(
  category: ConsentCategory,
  cats: ConsentCategories | undefined
): boolean | undefined {
  switch (category) {
    case 'strictly_necessary':
      return true
    case 'analytics':
      return cats?.analytics
    case 'functional':
      return cats?.functional
    case 'marketing':
      return cats?.marketing
  }
}

/**
 * Is a non-necessary category permitted to load?
 *
 * Resolution order (matches the reference spec):
 *   1. GPC active  → DENY non-necessary, absolutely (cannot be overridden).
 *   2. strictly_necessary → always permitted.
 *   3. not_required → permit.
 *   4. opt_out → permit unless the user explicitly opted out (default-on).
 *   5. consent_required → permit only on explicit opt-in.
 * Default-deny: anything unhandled is not permitted.
 */
export function isCategoryPermitted(
  category: ConsentCategory,
  jurisdiction: Jurisdiction,
  cats: ConsentCategories | undefined,
  gpc: boolean
): boolean {
  if (category === 'strictly_necessary') return true
  // GPC is an absolute opt-out for non-essential categories.
  if (gpc) return false

  const consented = categoryConsented(category, cats)
  switch (jurisdiction) {
    case 'not_required':
    case 'opt_out':
      // Default-on, but honor an explicit opt-out the user made via Cookie
      // settings (a stored `false` disables it; undefined/no-choice → on).
      return consented !== false
    case 'consent_required':
      // Opt-in: undefined/false → off.
      return consented === true
    default:
      return false
  }
}

/** Is a specific tracker permitted to initialize? */
export function isTrackerPermitted(
  tracker: Tracker,
  jurisdiction: Jurisdiction,
  cats: ConsentCategories | undefined,
  gpc: boolean
): boolean {
  return isCategoryPermitted(tracker.category, jurisdiction, cats, gpc)
}
